import React, {useCallback, useState} from "react";
import {useAppSelector} from "../../../app/hooks";
import {ChatEngine} from "../../../types";
import {
    createPendingPasteAttachment,
    isAttachablePaste,
    MAX_PASTE_ATTACHMENTS,
    normalizePastedText,
    PendingPasteAttachment,
} from "../utils/pasteAttachments";

/**
 * Holds the pastes that an input captures as attachments. A paste with more
 * lines than the threshold does not go into the textarea. A shorter paste
 * goes into the textarea as normal.
 *
 * The experimental engine has no attachment support. On that engine a paste
 * stays as text in the textarea and does not become a chip. A chip would not
 * be part of the request.
 */
export function usePasteAttachments() {
    const [attachments, setAttachments] = useState<PendingPasteAttachment[]>(
        []
    );
    const chatEngine = useAppSelector(
        (state) => state.session.chosenChatEngine
    );
    const attachmentsSupported = chatEngine !== ChatEngine.Experimental;

    const handlePaste = useCallback(
        (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
            if (!attachmentsSupported) return;
            const text = normalizePastedText(
                event.clipboardData.getData("text/plain")
            );
            if (!text || !isAttachablePaste(text)) return;
            // The paste is an attachment, so it does not go into the textarea.
            // This is true even when the code below refuses it for the limit
            // or as a duplicate.
            event.preventDefault();
            setAttachments((current) => {
                if (current.length >= MAX_PASTE_ATTACHMENTS) return current;
                const pending = createPendingPasteAttachment(text);
                const isDuplicate = current.some(
                    (attachment) => attachment.content === pending.content
                );
                return isDuplicate ? current : [...current, pending];
            });
        },
        [attachmentsSupported]
    );

    const removeAttachment = useCallback((id: string) => {
        setAttachments((current) =>
            current.filter((attachment) => attachment.id !== id)
        );
    }, []);

    const clearAttachments = useCallback(() => setAttachments([]), []);

    return {
        attachments,
        handlePaste,
        removeAttachment,
        clearAttachments,
        restoreAttachments: setAttachments,
    };
}
